import React, { useState } from 'react';
import { Check, Sparkles, Eye, Ban, Zap } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsApi } from '../lib/api';
import { toast } from 'sonner';

const PricingPage = () => {
  const { isAuthenticated, isPremium } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setLoading(true);
    try {
      const result = await paymentsApi.subscribe('premium', window.location.origin);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else if (result.status === 'active') {
        toast.success('Subscription activated!');
        setTimeout(() => navigate('/browse'), 1500);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to start subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="pricing-page">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#8B5CF6]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#00FF9D]/10 rounded-full blur-3xl" />
      </div>

      {/* Navbar */}
      <nav className="relative z-20 py-6 px-4 md:px-8 lg:px-[4%]">
        <div className="w-full flex items-center justify-between">
          <Link to="/" className="flex items-center"><img src="/reframes-logo.png" alt="reframes" className="h-9 md:h-11" /></Link>
          {isAuthenticated ? (
            <Link to="/browse" className="btn-secondary text-sm">Back to Browse</Link>
          ) : (
            <Link to="/login" className="btn-primary text-sm">Sign In</Link>
          )}
        </div>
      </nav>

      {/* Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8 py-6 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-16">
          <div className="inline-flex items-center gap-2 badge bg-[#8B5CF6]/20 mb-4">
            <Sparkles className="w-4 h-4" />
            Freemium Platform
          </div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-4 sm:mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Watch Free. <span className="text-[#8B5CF6]">Go Premium.</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
            Enjoy all content for free with ads, or upgrade to Premium for an uninterrupted experience.
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Tier */}
          <div className="admin-card p-8" data-testid="plan-free">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gray-800 flex items-center justify-center text-gray-400">
                <Eye className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Free
              </h2>
              <p className="text-gray-400">Watch everything, with ads</p>
            </div>

            <div className="text-center mb-8">
              <div className="flex items-end justify-center gap-1">
                <span className="text-5xl font-bold">$0</span>
                <span className="text-gray-400 mb-2">/forever</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                'Full access to all content',
                'HD streaming quality',
                'Browse & discover films',
                'Rate and review content',
                'Create a My List',
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#00FF9D]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[#00FF9D]" />
                  </div>
                  <span className="text-gray-300">{feature}</span>
                </li>
              ))}
              <li className="flex items-center gap-3 text-gray-500">
                <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <Ban className="w-3 h-3 text-gray-600" />
                </div>
                <span>Includes advertising</span>
              </li>
            </ul>

            <Link
              to={isAuthenticated ? '/browse' : '/register'}
              className="block w-full py-4 rounded-full font-semibold text-center border border-white/20 text-white hover:bg-white/5 transition-all"
              data-testid="plan-free-btn"
            >
              {isAuthenticated ? 'Start Watching' : 'Sign Up Free'}
            </Link>
          </div>

          {/* Premium Tier */}
          <div className="admin-card relative p-8 ring-2 ring-[#8B5CF6]" data-testid="plan-premium">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="badge bg-[#8B5CF6] text-white border-[#8B5CF6]">Recommended</span>
            </div>

            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center text-[#8B5CF6]">
                <Zap className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Premium
              </h2>
              <p className="text-gray-400">Ad-free, uninterrupted viewing</p>
            </div>

            <div className="text-center mb-8">
              <div className="flex items-end justify-center gap-1">
                <span className="text-5xl font-bold">$3.99</span>
                <span className="text-gray-400 mb-2">/month</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                'Everything in Free tier',
                'Zero ads - completely ad-free',
                'Uninterrupted viewing experience',
                'Support creators directly',
                'Cancel anytime',
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#00FF9D]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[#00FF9D]" />
                  </div>
                  <span className="text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={handleSubscribe}
              disabled={loading || isPremium}
              className="w-full py-4 rounded-full font-semibold btn-white"
              data-testid="subscribe-premium"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="spinner w-5 h-5" />
                  Processing...
                </span>
              ) : isPremium ? (
                'Already Premium'
              ) : (
                'Subscribe for $3.99/mo'
              )}
            </button>
          </div>
        </div>

        {/* Revenue Info */}
        <div className="mt-16 max-w-2xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            70% of all subscription and ad revenue is shared with creators based on watch time.
            Your subscription directly supports the creators you love.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
